import React, { useEffect, useState } from 'react';
import { Col, Row } from '@gjensidige/core-grid';
import {
  apiCallBegan,
  APICallMethods
} from '@gjensidige/se-redux-api-middleware';

import { useAppDispatch, useAppSelector } from '../hooks/appHooks';
import { ROUTES } from '../lib/constants';
import APP_CONSTANTS, { API_OPERATIONS, SESSION_KEYS } from '../lib/constants/appConstants';
import API_ENDPOINTS from '../lib/constants/endpoints';
import {
  getSelectedPackages,
  onApiBegan,
  onApiError,
  onApiSuccess,
  updatePurchaseStatus
} from '../store/slices/offerSlice';
import tracker from '../tracking';
import { ErrorPageHandler } from '../components/Common/ErrorPage/ErrorPageHandler';
import { PageLoader } from '../components/Common/Loader/PageLoader';
import { SectionLoader } from '../components/Common/Loader/SectionLoader';
import { WelcomeBackModal } from '../components/Summary/WelcomeBackModal';
import { FooterContainer } from '../containers/FooterContainer';
import { HeaderContainer } from '../containers/HeaderContainer';
import { SaveOfferContainer } from '../containers/SaveOfferContainer';
import { SummaryContainer } from '../containers/SummaryContainer';
import { Package } from '../interfaces/Types';
import { AppConstants } from '../utils/AppConstants';
import { InsuranceHelper } from '../utils/InsuranceHelper';
import { TextHelper } from '../utils/TextHelper';
import { TrackingHelper } from '../utils/TrackingHelper';

interface SummaryPageProps {
  product: string;
}
export const SummaryPage: React.FC<SummaryPageProps> = (
  props: SummaryPageProps
) => {
  const dispatch = useAppDispatch();
  const isMetaDataReceived = useAppSelector(
    (state) => state.insurance.isMetaDataReceived
  );
  const insurancePackages = useAppSelector((state) =>
    state.insurance.packages.filter((item: Package) => !item.isPlusPackage)
  );
  const productNumber = useAppSelector(
    (state) => state.insurance.offer.productCode
  );
  const offerInfo = useAppSelector((state) => state.insurance.offer);
  const [isViewTracked, setIsViewTracked] = useState(false);
  const selectedPackages = useAppSelector(getSelectedPackages);
  useEffect(() => {
    InsuranceHelper.setPageTitle(
      TextHelper.getPageTitle(props.product, ROUTES.summaryPage)
    );
    const stepStatus = TextHelper.getHeaderStepLabels(
      props.product,
      ROUTES.summaryPage
    );
    dispatch(updatePurchaseStatus(stepStatus));
    tracker.setOptions({
      scenario_name: TrackingHelper.getScenarioNameFromProduct(props.product),
      scenario_step: 'summary'
    });
  }, []);
  useEffect(() => {
    if (insurancePackages.length && !isViewTracked) {
      trackSummaryPage(props.product);
      setIsViewTracked(true);
    }
  }, [insurancePackages]);

  const { policyNumber: policyNumberFromUrl } = InsuranceHelper.getPolicyAndChannelFromUrl();
  const trackSummaryPage = (productName: string) => {
    const { totalPrice, selectedPackageNames, selectedPackageYearlyPrices } =
      TrackingHelper.getEcommerceInfoToTrack(selectedPackages);
   
    const summaryPayLoad = {
      event_name: 'checkout',
      application_name: 'webshop',
      application_module: 'checkout',
      application_partner: 'gjensidige',
      application_version: '2.0',
      business_area: 'insurance',
      customer_segment: 'private',
      page_pathname: `/apps/${
        tracker.options.application_name
      }/checkout/${TrackingHelper.getScenarioNameFromProduct(
        productName
      )}/summary`,
      page_title: 'webshop-frontend-se',
      product_sku: [productNumber],
      product_variant_name: [selectedPackageNames.join(';')],
      product_variant_quantity: [`${selectedPackageNames.length}`],
      product_variant_total: [selectedPackageYearlyPrices.join(';')],
      product_name: TrackingHelper.getProductListToTrack(productName),
      product_brand: ['insurance'],
      product_category: [TrackingHelper.getProductCategoryToTrack(productName)],
      product_unit_price: [`${totalPrice ? totalPrice : 0}`],
      product_quantity: ['1'],
      scenario_name: TrackingHelper.getScenarioNameFromProduct(productName),
      scenario_step: 'summary',
      scenario_type: TrackingHelper.isFollowUpOffer(offerInfo?.policyNumber) ? 'follow-up-offer' : ''
    };
    tracker.trackView(summaryPayLoad);
  };
  useEffect(() => {
    if (isMetaDataReceived) {
      if (!insurancePackages.length) {
        //Landing from saved offer url from email
        if (policyNumberFromUrl) {
          sessionStorage.setItem(
            SESSION_KEYS.SAVED_POLICY,
            policyNumberFromUrl
          );
          dispatch(
            apiCallBegan({
              init: {
                headers: {
                  Applikasjon: APP_CONSTANTS.application
                }
              },
              url: API_ENDPOINTS.getSavedOfferSummaryURL(policyNumberFromUrl),
              apiOperation: API_OPERATIONS.GET_SAVED_OFFER_SUMMARY,
              method: APICallMethods.GET_JSON,
              onStart: onApiBegan.type,
              onSuccess: onApiSuccess.type,
              onError: onApiError.type
            })
          );
        } else {
          const policyDetails = InsuranceHelper.retrievePolicyInfoFromURL();
          if (policyDetails.customerNumber && policyDetails.policyNumber) {
            dispatch(
              apiCallBegan({
                init: {
                  headers: {
                    Applikasjon: APP_CONSTANTS.application
                  }
                },
                url: API_ENDPOINTS.getOfferSummaryURL(
                  policyDetails.policyNumber
                ),
                apiOperation: API_OPERATIONS.GET_OFFER_SUMMARY,
                method: APICallMethods.GET_JSON,
                onStart: onApiBegan.type,
                onSuccess: onApiSuccess.type,
                onError: onApiError.type
              })
            );
          }
        }
      }
    }
  }, [isMetaDataReceived]);

  const channel = InsuranceHelper.getChannelInfo();
  const isHgf = channel?.toLowerCase() === 'hgf';
  return (
    <PageLoader>
      <ErrorPageHandler>
        <div className="summary-page">
          <HeaderContainer currentPage="offer" productName={props.product} />
          <main>
            <SectionLoader></SectionLoader>
            <div className="mb-6 mt-2">
              <div className="summary-page-wrapper">
                <SummaryContainer productName={props.product} />
                <div className="summary-bottom-wrapper summary-container-wrapper">
                  {!isHgf && <SaveOfferContainer />}
                  {/* <Row>
                    <Col
                      xl={{ span: 8 }}
                      lg={{ span: 8 }}
                      md={{ span: 8 }}
                      sm="12"
                      className="mt-2  footer-section-wrapper"
                    > */}
                  <div className="footer-section-wrapper mt-4">
                    <FooterContainer
                      shouldDisplayGoBackButton
                      isNextButtonDisabled={false}
                    />
                  </div>
                  {/* </Col>
                  </Row> */}
                </div>
                {policyNumberFromUrl && (
                  <WelcomeBackModal
                    welcomeMessage={{
                      header: `${AppConstants.getAppText(
                        'webshop.customerGreeting'
                      )} ${offerInfo.customerName}`,
                      message: AppConstants.getAppText(
                        'webshop.summary.welcome.modalMessage'
                      )
                    }}
                    closeLabel="Stäng information"
                    modalLabel="Hämta erbjudande"
                  />
                )}
              </div>
            </div>
          </main>
        </div>
      </ErrorPageHandler>
    </PageLoader>
  );
};
